// Field-name paths as Conform writes them: "todos[0].title".
// Adapted from @conform-to/dom 1.19.4 (MIT), which `@conform-to/react` does
// not re-export.

export type PathSegment = string | number;

/** `"array[0].content"` → `['array', 0, 'content']`, `"todos[]"` → `['todos', '']`. */
export const getPaths = (path: string): PathSegment[] => {
  if (!path) return [];
  const token = /([^.[\]]+)|\[(\d*)\]/g;
  const segments: PathSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = token.exec(path))) {
    if (
      match.index !== lastIndex &&
      !(match.index === lastIndex + 1 && path[lastIndex] === '.')
    ) {
      throw new Error(`Invalid path syntax at ${lastIndex} in "${path}"`);
    }
    const [, key, index] = match;
    if (key !== undefined) {
      if (key === '__proto__' || key === 'constructor') {
        throw new Error(`Invalid path segment "${key}"`);
      }
      segments.push(key);
    } else {
      segments.push(index === '' ? '' : Number(index));
    }
    lastIndex = token.lastIndex;
  }
  if (lastIndex !== path.length) {
    throw new Error(`Invalid path syntax at ${lastIndex} in "${path}"`);
  }
  return segments;
};

/** `['array', 0, 'content']` → `"array[0].content"`, `['todos', '']` → `"todos[]"`. */
export const formatPaths = (segments: readonly PathSegment[]): string =>
  segments.reduce<string>((path, segment) => {
    if (segment === '') return `${path}[]`;
    if (typeof segment === 'number') return `${path}[${segment}]`;
    return path ? `${path}.${segment}` : segment;
  }, '');

/** The segments of `full` after `base`, or null when `full` is not inside it. */
export const getRelativePath = (
  full: readonly PathSegment[],
  base: readonly PathSegment[],
): PathSegment[] | null =>
  full.length >= base.length &&
  base.every((segment, index) => segment === full[index])
    ? full.slice(base.length)
    : null;
